import { useState } from "react";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import Overlay from "./Overlay";

const HUES = { gold: "#D4AF37", violet: "#8a5fbf", blue: "#4a6fd8", red: "#a52a2a" };

const MYSTERY_LINES = [
  "THEY COME FROM DIFFERENT WORLDS.",
  "THEY BELONG TO DIFFERENT STORIES.",
  "THEIR POWERS ARE DIFFERENT.",
  "THEIR PURPOSES ARE UNKNOWN.",
];

const Timeline = ({ graph = [] }) => {
  const [active, setActive] = useState(null);
  const edges = graph.flatMap((n) =>
    n.connections.map((c) => ({ from: n, to: graph.find((m) => m.id === c) })).filter((e) => e.to)
  );
  const ordered = [...graph].sort((a, b) => a.part.localeCompare(b.part));
  const titlesOf = (ids) =>
    ids.map((id) => graph.find((n) => n.id === id)?.title).filter(Boolean).join("  ·  ");

  const Node = ({ n }) => (
    <button
      data-testid={`graph-node-${n.id}`}
      onClick={() => setActive(n)}
      className="group relative flex items-center gap-5 border border-white/5 bg-white/[0.02] px-6 py-5 text-left transition-colors duration-500 hover:border-[#D4AF37]/30 w-full"
    >
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border font-tech text-[9px]"
        style={{ borderColor: `${HUES[n.hue]}66`, color: HUES[n.hue], boxShadow: `0 0 18px 2px ${HUES[n.hue]}33` }}
      >
        {n.part.split(" ")[1]}
      </span>
      <span>
        <span className="block text-base tracking-wider text-[#F5F5FA]">{n.title}</span>
        {n.connections.length > 0 && (
          <span className="mt-1 block font-mono text-[9px] tracking-[0.25em] text-[#606070] uppercase">
            ↓ connects to {titlesOf(n.connections)}
          </span>
        )}
        {n.connections.length === 0 && (
          <span className="mt-1 block font-mono text-[9px] tracking-[0.25em] text-[#D4AF37]/70 uppercase">
            ✦ where the threads converge
          </span>
        )}
      </span>
    </button>
  );

  return (
    <section id="timeline" data-testid="timeline-section" className="relative z-10 px-6 py-28 md:px-16 lg:px-24 lg:py-40">
      <SectionHeading
        title="THE CONNECTED UNIVERSE"
        eyebrow="Every story begins somewhere. But in the Cosmic Dreams Universe, no story exists alone."
      />

      <Reveal>
        <div className="relative hidden md:block" style={{ paddingTop: "56%" }} data-testid="story-graph">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {edges.map((e, i) => (
              <g key={i}>
                <line x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y} stroke={HUES[e.from.hue]} strokeOpacity="0.14" strokeWidth="0.7" />
                <line className="graph-flow" x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y} stroke={HUES[e.from.hue]} strokeOpacity="0.6" strokeWidth="0.18" />
              </g>
            ))}
          </svg>
          {graph.map((n) => (
            <button
              key={n.id}
              data-testid={`graph-node-desktop-${n.id}`}
              onClick={() => setActive(n)}
              className="group absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
              aria-label={`Open ${n.title}`}
            >
              <span
                className="flex h-14 w-14 lg:h-16 lg:w-16 items-center justify-center rounded-full border bg-[#030305]/80 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110"
                style={{ borderColor: `${HUES[n.hue]}88`, boxShadow: `0 0 24px 3px ${HUES[n.hue]}44` }}
              >
                <span className="font-tech text-[9px]" style={{ color: HUES[n.hue] }}>
                  {n.part.split(" ")[1]}
                </span>
              </span>
              <span
                className={`absolute left-1/2 w-44 -translate-x-1/2 text-center text-[11px] leading-snug tracking-wider text-[#F5F5FA]/85 transition-colors duration-500 group-hover:text-[#D4AF37] ${
                  n.y > 60 ? "bottom-full mb-3" : "top-full mt-3"
                }`}
              >
                {n.title}
              </span>
            </button>
          ))}
        </div>
      </Reveal>

      <div className="space-y-4 md:hidden" data-testid="story-graph-mobile">
        {ordered.map((n) => (
          <Reveal key={n.id}>
            <Node n={n} />
          </Reveal>
        ))}
      </div>

      <div className="mx-auto mt-32 max-w-3xl text-center" data-testid="big-mystery">
        <Reveal>
          <p className="font-tech text-[10px] tracking-[0.45em] text-[#D4AF37] uppercase">
            How are they all connected?
          </p>
        </Reveal>
        <div className="mt-8 space-y-3">
          {[
            "How are all these superheroes from different worlds coming together?",
            "What is the purpose behind their alliance?",
            "Who are they really facing?",
          ].map((q, i) => (
            <Reveal key={q} delay={0.08 * (i + 1)}>
              <p className="text-sm md:text-base font-light italic text-[#A0A0B0]">{q}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 space-y-4">
          {MYSTERY_LINES.map((line, i) => (
            <Reveal key={line} delay={0.1 * i}>
              <p className="font-serif text-lg md:text-2xl tracking-wide text-[#F5F5FA]/85">{line}</p>
            </Reveal>
          ))}
          <Reveal delay={0.5}>
            <p className="pt-4 font-serif text-2xl md:text-3xl tracking-wide text-[#D4AF37]">YET…</p>
          </Reveal>
          <Reveal delay={0.6}>
            <p className="font-serif text-xl md:text-3xl tracking-wide text-[#F5F5FA]">
              THEIR PATHS ARE BEGINNING TO CONVERGE.
            </p>
          </Reveal>
        </div>
        <div className="mt-16 space-y-3">
          {["WHY?", "WHO BROUGHT THEM TOGETHER?", "AND WHAT ARE THEY FACING?"].map((q, i) => (
            <Reveal key={q} delay={0.1 * i}>
              <p className="font-mono text-xs md:text-sm tracking-[0.35em] text-[#D4AF37] uppercase">{q}</p>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <p className="pt-6 font-mono text-[9px] tracking-[0.4em] text-[#606070] uppercase">
              The answer remains beyond the veil
            </p>
          </Reveal>
        </div>
      </div>

      <Overlay
        open={!!active}
        onClose={() => setActive(null)}
        title={active ? active.title : ""}
        label={active ? `${active.part} — Story Thread` : ""}
        testid="graph-overlay"
        z={98}
      >
        {active && (
          <div className="max-w-2xl space-y-8">
            <p className="text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">{active.desc}</p>
            <div className="border-t border-white/5 pt-6">
              <h4 className="font-tech text-[10px] uppercase text-[#D4AF37]/80">Place in the Cosmic Dreams Universe</h4>
              <p className="mt-3 text-sm font-light text-[#F5F5FA]/85">{active.place}</p>
            </div>
            <div className="border-t border-white/5 pt-6">
              <h4 className="font-tech text-[10px] uppercase text-[#D4AF37]/80">Connected Heroes</h4>
              <p className="mt-3 text-lg tracking-wider text-[#F5F5FA]">{active.heroes}</p>
            </div>
            <div className="border-t border-white/5 pt-6">
              <h4 className="font-tech text-[10px] uppercase text-[#D4AF37]/80">Connected Stories</h4>
              <p className="mt-3 text-sm font-light text-[#F5F5FA]/85">
                {active.connections.length > 0 ? titlesOf(active.connections) : "Where the threads converge."}
              </p>
            </div>
          </div>
        )}
      </Overlay>
    </section>
  );
};

export default Timeline;
